import { Action, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { LocalizationService } from 'src/app/services/localization/localization.service';
import { AlertService } from 'src/app/shared/store/alert/alert.service';
import { MappingUtilsService } from 'src/app/services/utils/mapping-utils.service';
import { Permission, PermissionRequest } from '../models/permission.model';
import { PermissionActions } from './permission.actions';
import { PermissionService } from '../services/permissions.service';
import { Menu } from 'src/app/shared/models/menu.model';
import { AuthenticationService } from '../../../services/authentication/authentication.service';
import { environment } from 'src/environments/environment';
import { AuthSelectors } from '../../login/store/auth.selectors';
import { patch } from '@ngxs/store/operators';
import { PermissionTable } from '../models/permission-table.model';
import { PermissionNode } from '../models/permission-node.model';

export class PermissionStateModel {
  public permissions: Permission[];
  public menu: Menu[];
  public permissionsTable: PermissionTable[];
  public permissionNodes: PermissionNode[];
  public isLoading: boolean;
  public error: string;
}

const defaults = {
  permissions: [],
  menu: [],
  permissionsTable: [],
  permissionNodes: [],
  isLoading: false,
  error: null,
};

@State<PermissionStateModel>({
  name: 'permission',
  defaults,
})
@Injectable()
export class PermissionState {
  constructor(
    private alertService: AlertService,
    private localizationService: LocalizationService,
    private permissionService: PermissionService,
    private authenticationService: AuthenticationService,
    public store: Store
  ) {}

  guidEmpty = '00000000-0000-0000-0000-000000000000';

  @Action(PermissionActions.GetPermissions) GetPermissions(
    ctx: StateContext<PermissionStateModel>,
    {}: PermissionActions.GetPermissions
  ) {
    return this.permissionService.getPermissions().subscribe({
      next: (response) =>
        ctx.dispatch(new PermissionActions.GetPermissionsSuccess(response)),
      error: (error) => ctx.dispatch(new PermissionActions.Error(error)),
    });
  }

  @Action(PermissionActions.GetPermissionsSuccess)
  getPermissionsSuccess(
    ctx: StateContext<PermissionStateModel>,
    { permissions }: PermissionActions.GetPermissionsSuccess
  ) {
    let newMenu: Menu[] = [];
    this.authenticationService.getValidToken().subscribe(
     (token) => {
      if(token === null) return this.authenticationService.logout();
      const userLevel = this.authenticationService.getUserLevel(token);
      newMenu = this.getMenuByLevel(permissions, userLevel);
     }
    )
    var permissionNodes = this.getPermissionNodes(permissions);
    ctx.patchState({
      permissions: permissions,
      isLoading: false,
      menu: newMenu,
      permissionsTable: this.getPermissionTable(permissionNodes, [], 0),
      permissionNodes: permissionNodes
    });
  }

  @Action(PermissionActions.UpdatePermission)
  updatePermission(ctx: StateContext<PermissionStateModel>, {}: PermissionActions.UpdatePermission) {
    const state = ctx.getState();
    this.alertService.confirm(
      this.localizationService.translate("permissions.title"),
      this.localizationService.translate('permissions.editPermissionMenusConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('permissions.editPermissionMenusConfirm')
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        let state = ctx.getState();
        const user = this.store.selectSnapshot(AuthSelectors.getUser);
        let updatedPermissions = this.createUpdatePermissions(state.permissionNodes);
        return this.permissionService.updatePermissions(updatedPermissions, user).subscribe({
          next: (res) => ctx.dispatch(new PermissionActions.UpdatePermissionSuccess()),
          error: (error) => {
            ctx.dispatch(new PermissionActions.Error(error));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(PermissionActions.UpdatePermissionSuccess)
  updatePermissionSuccess(ctx: StateContext<PermissionStateModel>, {}: PermissionActions.UpdatePermissionSuccess) {
    ctx.patchState({
      isLoading: false,
    });
  }

  @Action(PermissionActions.EditTableCell)
  editTableCell(ctx: StateContext<PermissionStateModel>, {levelPermissionId, level, isChecked}: PermissionActions.EditTableCell) {
    let state = ctx.getState();
    let permissions = state.permissions;
    let permissionNodes = state.permissionNodes;
    let newPermissionNodes = this.updateLevelActivePermission(permissionNodes, permissions, levelPermissionId, level, isChecked);
    let newPermissionsTable = this.getPermissionTable(newPermissionNodes, [], 0);

    ctx.setState(
      patch({
        permissionNodes: newPermissionNodes,
        permissionsTable: newPermissionsTable
      })
    );
  }

  @Action(PermissionActions.ExpandTableRow)
  expandTableRow(ctx: StateContext<PermissionStateModel>, {levelPermissionId}: PermissionActions.ExpandTableRow) {
    let state = ctx.getState();
    let permissionNodes = state.permissionNodes;
    let newPermissionNodes = this.updateIsExpandedPermissionTable(permissionNodes, levelPermissionId);
    let newPermissionTable = this.getPermissionTable(newPermissionNodes, [], 0);

    ctx.setState(
      patch({
        permissionNodes: newPermissionNodes,
        permissionsTable: newPermissionTable
      })
    );
  }

  @Action(PermissionActions.SetConfigurationDefaultState)
  setConfigurationDefaultState(
    ctx: StateContext<PermissionStateModel>, {}: PermissionActions.SetConfigurationDefaultState) {
      ctx.setState(
        patch({permissions : defaults.permissions
      })
    );
  }

  @Action(PermissionActions.SetDefaultState)
  setDefaultState(
    ctx: StateContext<PermissionStateModel>, {}: PermissionActions.SetDefaultState) {
      ctx.patchState(defaults);
  }

  @Action(PermissionActions.Error)
  onErrorOccurred(
    ctx: StateContext<PermissionStateModel>,
    { error }: PermissionActions.Error
  ) {
    ctx.patchState({
      error: error,
      isLoading: false,
    });
    MappingUtilsService.showError(this.getTitle(), error, this.alertService, this.localizationService);
  }

  private createUpdatePermissions(permissionNodes: PermissionNode[]): PermissionRequest[] {
    let permissions: PermissionRequest[] = [];
    permissionNodes.map((permissionNode) => {
      if(permissionNode.isEdited) {
        let permission = new PermissionRequest();
        permission.levelModulePermissionId = permissionNode.levelModulePermissionId;
        permission.Level0 = permissionNode.level0 === true;
        permission.Level1 = permissionNode.level1 === true;
        permission.Level2 = permissionNode.level2 === true;
        permission.Level3 = permissionNode.level3 === true;
        permission.Level4 = permissionNode.level4 === true;
        permission.Level5 = permissionNode.level5 === true;
        permission.Level6 = permissionNode.level6 === true;
        permissions.push(permission);
      }
      if(permissionNode.childrens.length > 0) {
        permissions.push(...this.createUpdatePermissions(permissionNode.childrens));
      }
    });
    return permissions;
  }

  private getMenuByLevel(permissions: Permission[], level: string) {
    let permissionsByLevel = permissions.filter(
      (permission) => permission.levelPermissions.includes(level)
    );
    return this.getMenuByParentId(this.guidEmpty, permissionsByLevel);
  }


  private getMenuByParentId(parentId: string, permissions: Permission[]): Menu[]  {
    let childPermissions = permissions.filter((permission) => permission.parentId === parentId);
    if(childPermissions.length === 0) return [];
    let menu: Menu[] = [];
    childPermissions.sort((a,b) => a.position - b.position).forEach((permission) => {
      let menuElement = new Menu();
      menuElement.id = permission.levelModulePermissionId;
      menuElement.path = this.getPermissionsPath(permission); 
      menuElement.name = permission.name;
      menuElement.menus = this.getMenuByParentId(permission.levelModulePermissionId, permissions);
      menuElement.isSgc1Url = permission.redirectToSgc1;
      menu.push(menuElement);
    });
    return menu;
  }

  private getPermissionNodes(permissions: Permission[]): PermissionNode[] {
    return this.getPermissionNodeByParentId(this.guidEmpty, permissions);
  }

  private getPermissionNodeByParentId(parentId: string, permissions: Permission[]): PermissionNode[] {
    let childPermissions = permissions.filter((permission) => permission.parentId === parentId);
    if(childPermissions.length === 0) return [];
    let permissionNodes: PermissionNode[] = [];
    childPermissions.sort((a,b) => a.position - b.position).forEach((permission) => {
      let permissionNode = new PermissionNode();
      permissionNode.levelModulePermissionId = permission.levelModulePermissionId;
      permissionNode.urlSgc1 = permission.urlSgc1;
      permissionNode.urlSgc2 = permission.urlSgc2;
      permissionNode.name = permission.name;
      permissionNode.redirectToSgc1 = permission.redirectToSgc1;
      permissionNode.level0 = permission.levelPermissions.includes("0");
      permissionNode.level1 = permission.levelPermissions.includes("1");
      permissionNode.level2 = permission.levelPermissions.includes("2");
      permissionNode.level3 = permission.levelPermissions.includes("3");
      permissionNode.level4 = permission.levelPermissions.includes("4");
      permissionNode.level5 = permission.levelPermissions.includes("5");
      permissionNode.level6 = permission.levelPermissions.includes("6");
      permissionNode.childrens = this.getPermissionNodeByParentId(permission.levelModulePermissionId, permissions);
      permissionNode.isEdited = false;
      permissionNode.isExpanded = false;
      permissionNodes.push(permissionNode);
    });
    return permissionNodes;
  }

  private getPermissionTable(permissionNodes: PermissionNode[], items: PermissionTable[], index: number) {
    permissionNodes.forEach(permissionNode => {
      if(!items) items = [];
      let newPermissionTable = this.getPermissionTableLine(permissionNode);
      newPermissionTable.index = index;
      items.push(newPermissionTable);
      if(permissionNode.isExpanded && permissionNode.childrens && permissionNode.childrens.length > 0) {
        this.getPermissionTable(permissionNode.childrens, items, index+1);
      }
    });
    return items;
  }

  private getPermissionTableLine(permissionNode: PermissionNode) {
    let permissionTable = new PermissionTable();
    permissionTable.levelModulePermissionId = permissionNode.levelModulePermissionId;
    permissionTable.name = permissionNode.name;
    permissionTable.urlSgc1 = permissionNode.urlSgc1;
    permissionTable.urlSgc2 = permissionNode.urlSgc2;
    permissionTable.redirectToSgc1 = permissionNode.redirectToSgc1;
    permissionTable.index = permissionNode.index;
    permissionTable.level0 = permissionNode.level0;
    permissionTable.level1 = permissionNode.level1;
    permissionTable.level2 = permissionNode.level2;
    permissionTable.level3 = permissionNode.level3;
    permissionTable.level4 = permissionNode.level4;
    permissionTable.level5 = permissionNode.level5;
    permissionTable.level6 = permissionNode.level6;
    permissionTable.isEdited = permissionNode.isEdited;
    permissionTable.isParent = permissionNode.childrens.length > 0;
    permissionTable.isExpanded = permissionNode.isExpanded;
    return permissionTable;
}


  private updateIsExpandedPermissionTable(permissionNode: PermissionNode[], levelPermissionId: string) {
    return permissionNode.map((permissionTable) => {
      let newPermissionTable = {...permissionTable};
      if(newPermissionTable.levelModulePermissionId === levelPermissionId) {
        newPermissionTable.isExpanded = !newPermissionTable.isExpanded;
      } else if(newPermissionTable.childrens.length > 0) {
        newPermissionTable.childrens = this.updateIsExpandedPermissionTable(newPermissionTable.childrens, levelPermissionId);
      }
      return newPermissionTable;
    });
  }

  private updateLevelActivePermission(permissionNodes: PermissionNode[], permissions: Permission[], levelPermissionId: string, level: string, isChecked: boolean): PermissionNode[] {
    let newPermissionNodes = permissionNodes.map((permissionNode) => {
      let newPermissionNode = {...permissionNode};
      if(newPermissionNode.levelModulePermissionId === levelPermissionId || levelPermissionId === this.guidEmpty) {
        newPermissionNode = this.changeLevelPermission(newPermissionNode, permissions, level, isChecked);
      } else if(newPermissionNode.childrens.length > 0) {
        newPermissionNode.childrens = this.updateLevelActivePermission(newPermissionNode.childrens, permissions, levelPermissionId, level, isChecked);
      }
      return newPermissionNode;
    });

    return this.updateParentNodes(newPermissionNodes, permissions, levelPermissionId, level);
  }

  private changeLevelPermission(permissionNode: PermissionNode, permissions: Permission[], level: string, isChecked: boolean): PermissionNode {
    let newPermissionNode = {...permissionNode};
    newPermissionNode = this.getNewPermissionNode(newPermissionNode, permissions, level, isChecked);
    if(newPermissionNode.childrens.length > 0) {
      let newSubPermissions = newPermissionNode.childrens.map((subPermission) => {
        return this.changeLevelPermission(subPermission, permissions, level, isChecked);
      });
      newPermissionNode.childrens = newSubPermissions;
    }
    return newPermissionNode;
  }

  private getNewPermissionNode(permissionNode: PermissionNode, permissions: Permission[], level: string, isChecked: boolean | 'indeterminate'): PermissionNode {
    let newPermissionNode = {...permissionNode};
    newPermissionNode.isEdited = this.getIsEdited(permissionNode, permissions, level, isChecked);
    if(level === "0") newPermissionNode.level0 = isChecked;
    if(level === "1") newPermissionNode.level1 = isChecked;
    if(level === "2") newPermissionNode.level2 = isChecked;
    if(level === "3") newPermissionNode.level3 = isChecked;
    if(level === "4") newPermissionNode.level4 = isChecked;
    if(level === "5") newPermissionNode.level5 = isChecked;
    if(level === "6") newPermissionNode.level6 = isChecked;
    return newPermissionNode;
  }

  private updateParentNodes(permissionNodes: PermissionNode[], permissions: Permission[], levelPermissionId: string, level: string): PermissionNode[] {
    let current = permissions.find((permission) => permission.levelModulePermissionId === levelPermissionId);
    if(!current || current.parentId === this.guidEmpty) return permissionNodes;
    
    let newPermissionNodes = this.updateParrentPermission(permissionNodes, permissions, current.parentId, level);
    
    return this.updateParentNodes(newPermissionNodes, permissions, current.parentId, level);
  }

  private updateParrentPermission(permissionNodes: PermissionNode[], permissions: Permission[], parentId: string, level: string): PermissionNode[] {
    return permissionNodes.map((permissionNode) => {
      let newPermissionNode = {...permissionNode} as PermissionNode;
      if(newPermissionNode.levelModulePermissionId === parentId && newPermissionNode.childrens.length > 0) {
        let isChecked = newPermissionNode.childrens.every((subPermission) => this.getIscheckedPermission(subPermission, level) === true);
        let someChecked = newPermissionNode.childrens.some((subPermission) => this.getIscheckedPermission(subPermission, level) === true);
        let newValue: boolean | 'indeterminate' = isChecked ? true : someChecked ? 'indeterminate' : false;
        newPermissionNode = this.getNewPermissionNode(newPermissionNode, permissions, level, newValue);
      } else if(newPermissionNode.childrens.length > 0) {
        newPermissionNode.childrens = this.updateParrentPermission(newPermissionNode.childrens, permissions, parentId, level);
      }
      return newPermissionNode;
    });
  }

  private getIscheckedPermission(permissionNode: PermissionNode, level: string): boolean | 'indeterminate' {
    if(level === "0") return permissionNode.level0;
    if(level === "1") return permissionNode.level1;
    if(level === "2") return permissionNode.level2;
    if(level === "3") return permissionNode.level3;
    if(level === "4") return permissionNode.level4;
    if(level === "5") return permissionNode.level5;
    if(level === "6") return permissionNode.level6;
    return false;
  }

  private getIsEdited(permissionNode: PermissionNode, permissions: Permission[],  level: string, isChecked: boolean | 'indeterminate') {
    let permission = permissions.find((permission) => permission.levelModulePermissionId === permissionNode.levelModulePermissionId);
    return permission.levelPermissions.includes(level) !== isChecked;
  }

  private getPermissionsPath(permission: Permission) {
    let sgc1Url = environment.sgc1Url;
    if (permission.redirectToSgc1) return sgc1Url + permission.urlSgc1;
    return permission.urlSgc2;
  }

  private getTitle() {
    return this.localizationService.translate('permissions.title');
  }
}
